import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { useAffiliateTheme } from '../../context/AffiliateThemeContext';

const AffiliateLoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useAffiliateTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError(t('affiliate.login.error.allFieldsRequired'));
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Utilise l'objet auth importé du fichier de configuration Firebase
      await signInWithEmailAndPassword(auth, email, password);
      
      // Navigate to dashboard on successful login
      navigate('/affiliate/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle specific error codes
      switch(error.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          setError(t('affiliate.login.error.invalidCredentials'));
          break;
        case 'auth/too-many-requests':
          setError(t('affiliate.login.error.tooManyAttempts'));
          break;
        case 'auth/user-disabled':
          setError(t('affiliate.login.error.accountDisabled'));
          break;
        default:
          setError(t('affiliate.login.error.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/affiliate/reset-password');
  };

  return (
    <PageContainer>
      <LoginContainer
        as={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <LoginHeader>
          <h1>{t('affiliate.login.title')}</h1>
          <p>{t('affiliate.login.subtitle')}</p>
        </LoginHeader>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <FormContainer onSubmit={handleLogin}>
          <FormGroup>
            <FormLabel htmlFor="email">{t('affiliate.login.email')}</FormLabel>
            <FormInput
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <FormLabel htmlFor="password">{t('affiliate.login.password')}</FormLabel>
            <FormInput
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </FormGroup>
          
          <ForgotPassword type="button" onClick={handleForgotPassword}>
            {t('affiliate.login.forgotPassword')}
          </ForgotPassword>
          
          <FormButton type="submit" disabled={isLoading}>
            {isLoading ? t('affiliate.login.loggingIn') : t('affiliate.login.loginButton')}
          </FormButton>
        </FormContainer>
        
        <SignupLink>
          {t('affiliate.login.noAccount')}
          <Link to="/affiliate/signup"> {t('affiliate.login.createAccount')}</Link>
        </SignupLink>
        
        <BackLink>
          <Link to="/affiliate">
            &larr; {t('affiliate.login.backToAffiliate')}
          </Link>
        </BackLink>
      </LoginContainer>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background-color: #f8f9fa;
`;

const LoginContainer = styled.div`
  max-width: 450px;
  width: 100%;
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  padding: 40px;
  
  @media (max-width: 500px) {
    padding: 30px 20px;
  }
`;

const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
  
  h1 {
    margin-bottom: 10px;
    color: #333;
  }
  
  p {
    color: #666;
  }
`;

const ErrorMessage = styled.div`
  background-color: #ffe0e0;
  color: #d32f2f;
  padding: 10px 15px;
  border-radius: 5px;
  margin-bottom: 20px;
  font-size: 0.9rem;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #333;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  transition: border-color 0.3s;
  
  &:focus {
    border-color: ${({ theme }) => theme.affiliate.colors.primary};
    outline: none;
  }
`;

const ForgotPassword = styled.button`
  align-self: flex-end;
  background: none;
  border: none;
  color: ${({ theme }) => theme.affiliate.colors.primary};
  font-size: 0.9rem;
  margin-bottom: 20px;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const FormButton = styled.button`
  background: ${({ theme }) => theme.affiliate.gradients.primary};
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 5px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SignupLink = styled.p`
  text-align: center;
  margin-top: 25px;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.affiliate.colors.text.secondary};
  
  a {
    color: ${({ theme }) => theme.affiliate.colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const BackLink = styled.div`
  text-align: center;
  margin-top: 30px;
  font-size: 0.9rem;
  
  a {
    color: ${({ theme }) => theme.affiliate.colors.text.secondary};
    text-decoration: none;
    transition: color 0.3s;
    
    &:hover {
      color: ${({ theme }) => theme.affiliate.colors.primary};
    }
  }
`;

export default AffiliateLoginPage;